import React, { createElement, PropsWithChildren, useState, createContext } from 'react';
import { ThemeProvider } from 'styled-components';
import { defaultTheme, GlobalStyle } from 'src/utils/styles';
import { BrowserRouter } from 'react-router-dom';
import Routes from 'src/Routes';
import useStripePromise from 'src/hooks/useStripePropmise';
import { Elements } from '@stripe/react-stripe-js';
import { QueryClientProvider, QueryClient } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import Modal, { TModalProps } from 'src/components/Modal';
import omit from 'lodash/omit';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export const initialModalProps: PropsWithChildren<TModalProps> = {
  isOpen: false,
  title: undefined,
  children: undefined,
  type: 'default',
};

export const AppContext = createContext({
  modal: {
    props: initialModalProps,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    openModal: (nextProps: Partial<PropsWithChildren<TModalProps>>) => {
      return;
    },
    closeModal: () => {
      return;
    },
  },
});

export default function App() {
  const stripePromise = useStripePromise();

  const [modalProps, setModalProps] = useState<PropsWithChildren<TModalProps>>(initialModalProps);

  const openModal = (nextProps: Partial<PropsWithChildren<TModalProps>>) => {
    setModalProps((prevProps) => ({ ...omit(prevProps, ['onAfterClose', 'onAfterOpen']), ...nextProps, isOpen: true }));
  };

  const closeModal = () => {
    setModalProps((prevProps) => ({ ...prevProps, isOpen: false }));
  };

  return (
    <AppContext.Provider value={{ modal: { props: modalProps, openModal, closeModal } }}>
      <Elements stripe={stripePromise}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={defaultTheme}>
            <BrowserRouter>
              <Routes />
              <GlobalStyle />
            </BrowserRouter>
            {createElement(Modal, modalProps)}
          </ThemeProvider>
          {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen />}
        </QueryClientProvider>
      </Elements>
    </AppContext.Provider>
  );
}
